import { ref, computed, inject } from 'vue';
import { defineStore } from 'pinia';
import { useStoreOrder } from './order';
import { useStoreCustomer } from './customer';
import { useRouter } from 'vue-router';
import { useApi } from '@/plugins/api';
import { insertScripts } from '@/utils/scripts';

const LOGGER_TAG = 'restaurant-store';

export const useStoreRestaurant = defineStore('restaurant', () => {
  let isLoading = ref(false);
  let restaurant = ref(null);
  let mapDetails = ref(null);
  let posDetails = ref(null);
  let promotions = ref(null);

  let menu = ref(null);

  let api = useApi();
  let oStore = useStoreOrder();
  let customerStore = useStoreCustomer();
  const logger = inject('logger');

  async function getInfo() {
    try {
      let { data } = await api.get('/restaurant');
      restaurant.value = data;
      if (data?.scripts) {
        injectScritps();
      }
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }

  async function injectScritps() {
    // set a timeout in order to wait for isLoading to be set to true
    while (isLoading.value) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    let scripts = restaurant.value.scripts;
    if (scripts.head) {
      insertScripts(scripts.head, 'head');
    }
    if (scripts.footer) {
      insertScripts(scripts.footer, '#footer');
    }
  }

  async function getMapDetails() {
    try {
      let { data } = await api.get('/restaurant/map_info');
      mapDetails.value = data;
      return data;
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }

  async function getPosDetails() {
    let pos = oStore.pos;
    try {
      if (!pos) {
        return;
      }
      let posId = pos.id;
      if (!pos.is_subrestaurant) {
        posId = '';
      }

      let { data } = await api.get(`/restaurant/pos_details/${posId}`);
      posDetails.value = data;
      return data;
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }

  async function getCategories(onlyCategories = false) {
    try {
      if (!oStore.pos) {
        return;
      }

      let posId = oStore.pos.id;
      let { data } = await api.get(`/restaurant/categories/${posId}`, {
        params: {
          only_categories: onlyCategories ? 1 : 0
        }
      });
      menu.value = data;
      return data;
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }

  async function getProducts() {
    try {
      if (!oStore.pos) {
        return;
      }

      if (!oStore.category) {
        return;
      }

      let posId = oStore.pos.id;
      let categoryId = oStore.category.id;
      let { data } = await api.get(`/restaurant/products/${posId}/${categoryId}`);
      return data.products;
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }

  async function getPromotions() {
    try {
      let { data } = await api.get(`/restaurant/promotions`);
      promotions.value = data;
      return data;
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }

  async function getProductDetails(productId) {
    try {
      if (!oStore.pos) {
        return;
      }
      let { data } = await api.get(`/restaurant/product/${productId}`);
      return data;
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }

  async function loadCustomCSS() {
    try {
      let { data } = await api.get('/restaurant/theme/customcss');
      let uri = data.custom_css_uri;
      let file = document.createElement('link');
      file.rel = 'stylesheet';
      file.href = uri;
      document.head.appendChild(file);
      return data;
    } catch (error) {
      logger.error(LOGGER_TAG, error);
    }
  }

  const poss = computed(() => {
    return mapDetails.value?.poss;
  });

  const hasReward = computed(() => {
    if (!restaurant.value) {
      return false;
    }

    let loyalty = restaurant.value.loyalty || customer.value?.loyalty;
    if (!loyalty) {
      return false;
    }

    return true;
  });

  const hasPromotions = computed(() => {
    return promotions.value?.list?.length;
  });

  const customer = computed(() => {
    return customerStore.customer;
  });

  return {
    restaurant, isLoading, mapDetails, posDetails, promotions, poss,
    menu, hasReward, hasPromotions,
    getMapDetails, getInfo, getPosDetails, getCategories,
    getPromotions, getProducts, loadCustomCSS, getProductDetails
  }
})
